import { useEffect, useMemo, useState } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import {
  platformCreateTraining,
  platformDeleteTraining,
  listTrainings,
  listTenants,
  listWorksites,
} from "../services/api";
import { loadSession } from "../auth";
import { useAdminScope } from "../hooks/useAdminScope";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

export default function PlatformTrainings() {
  const { t } = useTranslation();
  const user = loadSession()?.user;
  const isPlatformAdmin =
    user?.derived?.isPlatformAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("platform_admin"));

  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Tenant/worksite context — persisted via useAdminScope
  const { tenantId, setTenantId, worksiteId, setWorksiteId } = useAdminScope(user?.id);

  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState("");
  const [worksites, setWorksites] = useState([]);
  const [worksitesLoading, setWorksitesLoading] = useState(false);
  const [worksitesError, setWorksitesError] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    slug: "",
    title: "",
    description: "",
    visibility: false,
    tenantId: null, // null = global
  });

  const nav = useNavigate();

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const { items } = await listTrainings({ q, tenantId: tenantId, page: 1, pageSize: 50 });
      setRows(items || []);
    } catch (e) {
      setErr(e?.message || t("platform_trainings.error_generic"));
    } finally {
      setLoading(false);
    }
  }

  // Load tenants on mount
  useEffect(() => {
    if (!isPlatformAdmin) return;
    setTenantsLoading(true);
    setTenantsError("");
    listTenants()
      .then((data) => setTenants(data?.tenants || []))
      .catch((e) => setTenantsError(e?.message || t("platform_dashboard.error_tenants")))
      .finally(() => setTenantsLoading(false));
  }, [isPlatformAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate saved tenantId once tenants are loaded
  useEffect(() => {
    if (!tenantId || tenants.length === 0) return;
    const ids = tenants.map((tn) => String(tn.tenant_id ?? ""));
    if (!ids.includes(String(tenantId))) setTenantId("");
  }, [tenants, tenantId, setTenantId]);

  // Load worksites when tenant changes
  useEffect(() => {
    setWorksites([]);
    setWorksitesError("");
    setRows([]);
    if (!tenantId) return;
    setWorksitesLoading(true);
    listWorksites({ tenantId })
      .then((data) => {
        const items = data?.items || [];
        setWorksites(items);
      })
      .catch((e) => setWorksitesError(e?.message || t("platform_dashboard.error_worksites")))
      .finally(() => setWorksitesLoading(false));
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate saved worksiteId and auto-select if exactly one worksite
  useEffect(() => {
    if (worksites.length === 0) return;
    if (worksiteId) {
      const ids = worksites.map((w) => String(w.id ?? w.worksite_id ?? ""));
      if (!ids.includes(String(worksiteId))) setWorksiteId("");
    } else if (worksites.length === 1) {
      setWorksiteId(String(worksites[0].id ?? worksites[0].worksite_id ?? ""));
    }
  }, [worksites, worksiteId, setWorksiteId]);

  useEffect(() => {
    if (!isPlatformAdmin || !tenantId) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, isPlatformAdmin, tenantId]);

  const list = useMemo(() => rows, [rows]);

  async function createTraining(e) {
    e?.preventDefault?.();
    setCreating(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        visibility: !!form.visibility,
        tenantId: form.tenantId || null,
        steps: [],
      };
      if (!payload.slug || !payload.title) {
        alert(t("platform_trainings.required_slug_title"));
        setCreating(false);
        return;
      }
      await platformCreateTraining(payload);
      setShowCreate(false);
      setForm({ slug: "", title: "", description: "", visibility: false, tenantId: null });
      await load();
      nav(`/platform/trainings`);
    } catch (e2) {
      alert(e2?.message || t("platform_trainings.create_failed"));
    } finally {
      setCreating(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm(t("platform_trainings.confirm_delete"))) return;
    try {
      await platformDeleteTraining(id, { force: false });
      await load();
    } catch (e) {
      alert(e?.message || t("platform_trainings.delete_failed"));
    }
  }

  if (!isPlatformAdmin) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-slate-600">
            <Trans i18nKey="platform_trainings.need_role">
              You need the <code>platform_admin</code> role to manage trainings.
            </Trans>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{t("platform_trainings.title")}</h1>
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("platform_trainings.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button onClick={() => setShowCreate((v) => !v)}>
            {showCreate ? t("platform_trainings.btn_close") : t("platform_trainings.btn_new")}
          </Button>
        </div>
      </div>

      {/* Tenant/worksite context selector */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col gap-1 min-w-[220px]">
            <label className="block text-xs text-slate-500 font-medium mb-1">
              {t("platform_dashboard.tenant_label")}
            </label>
            {tenantsLoading ? (
              <p className="text-sm text-slate-500">{t("platform_dashboard.loading_tenants")}</p>
            ) : tenantsError ? (
              <p className="text-sm text-red-600">{tenantsError}</p>
            ) : (
              <select
                className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              >
                <option value="">{t("platform_dashboard.select_tenant")}</option>
                {tenants.map((tn) => (
                  <option key={tn.tenant_id} value={tn.tenant_id}>
                    {tn.tenant_id} ({tn.user_count ?? 0})
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex flex-col gap-1 min-w-[220px]">
            <label className="block text-xs text-slate-500 font-medium mb-1">
              {t("platform_dashboard.worksite_label")}
            </label>
            {tenantId && worksitesLoading ? (
              <p className="text-sm text-slate-500">{t("platform_dashboard.loading_worksites")}</p>
            ) : tenantId && worksitesError ? (
              <p className="text-sm text-red-600">{worksitesError}</p>
            ) : tenantId && worksites.length === 0 ? (
              <p className="text-sm text-slate-400">{t("platform_dashboard.no_worksites")}</p>
            ) : (
              <select
                className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:opacity-40"
                value={worksiteId}
                onChange={(e) => setWorksiteId(e.target.value)}
                disabled={!tenantId || worksites.length === 0}
              >
                <option value="">{t("platform_dashboard.all_worksites")}</option>
                {worksites.map((w) => {
                  const id = w.id ?? w.worksite_id ?? "";
                  return (
                    <option key={id} value={id}>
                      {w.name ?? id}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
        </div>
      </Card>

      {showCreate && (
        <Card title={t("platform_trainings.create_title")}>
          <form className="grid sm:grid-cols-2 gap-3" onSubmit={createTraining}>
            <div>
              <label className="block text-xs text-slate-500 font-medium mb-1">
                {t("platform_trainings.label_slug")}
              </label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder={t("platform_trainings.ph_slug")}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 font-medium mb-1">
                {t("platform_trainings.label_title")}
              </label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t("platform_trainings.ph_title")}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-slate-500 font-medium mb-1">
                {t("platform_trainings.label_description")}
              </label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t("platform_trainings.ph_description")}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="vis"
                type="checkbox"
                checked={form.visibility}
                onChange={(e) => setForm({ ...form, visibility: e.target.checked })}
              />
              <label htmlFor="vis" className="text-sm text-slate-700">
                {t("platform_trainings.label_visible")}
              </label>
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit" disabled={creating}>
                {creating ? t("platform_trainings.btn_creating") : t("platform_trainings.btn_create")}
              </Button>
              <Button variant="ghost" type="button" onClick={() => setShowCreate(false)}>
                {t("platform_trainings.btn_cancel")}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card>
        {!tenantId ? (
          <div className="text-sm text-slate-500 py-2">{t("platform_trainings.select_tenant_first")}</div>
        ) : loading ? (
          <div className="text-sm text-slate-500">{t("platform_trainings.loading")}</div>
        ) : err ? (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</div>
        ) : list.length ? (
          <ul className="divide-y divide-slate-100">
            {list.map((tItem) => (
              <li key={tItem.id} className="py-3.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{tItem.title}</div>
                  <div className="text-sm text-slate-500 truncate">{tItem.description}</div>
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-2">
                    <span>{t("platform_trainings.meta_slug", { slug: tItem.slug })}</span>
                    <span>
                      {t("platform_trainings.meta_steps", {
                        count: tItem.stepsCount ?? "—"
                      })}
                    </span>
                    {tItem.visibility ? (
                      <span className="px-2 py-0.5 rounded-full text-xs border bg-emerald-50 border-emerald-200 text-emerald-700">
                        {t("platform_trainings.badge_visible")}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs border bg-slate-100 border-slate-200 text-slate-500">
                        {t("platform_trainings.badge_hidden")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/platform/trainings/${tItem.id}`}>
                    <Button variant="secondary" size="sm">{t("platform_trainings.btn_open")}</Button>
                  </Link>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(tItem.id)}>
                    {t("platform_trainings.btn_delete")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-slate-500 py-4 text-center">{t("platform_trainings.no_trainings")}</div>
        )}
      </Card>
    </div>
  );
}
